import L from "leaflet";
import type { Polygon, Position } from "geojson";

/** Radio de snap en píxeles: a qué distancia del borde aparece el handle.
 *  Se exporta porque el modo Editar del paso 4 usa el mismo radio: las dos
 *  ediciones tienen que sentirse iguales. */
export const SNAP_PX = 14;

/** Diámetro del handle, en píxeles. Son 2 más que el vértice EN REPOSO (11, el
 *  `Size` del Mark de `sextante_vertices_poligono.sld`), y esa diferencia es
 *  deliberada: junto con el color verde es lo que se ve como el enganche del
 *  snap. El vértice que el cursor agarró crece y cambia de color; los demás
 *  siguen blancos y chicos, dibujados por el WMS. */
export const SNAP_PX_ICONO = 13;

/** El handle del vértice enganchado. Es el ÚNICO elemento que este motor pone
 *  en el mapa: el polígono y los vértices en reposo los dibuja GeoServer.
 *
 *  **`leaflet-div-icon` va a mano en el `className`, y es obligatorio.**
 *  `L.DivIcon` usa esa clase como valor POR DEFECTO de `options.className`, así
 *  que pasar uno propio la reemplaza en vez de sumarse. El CSS de estos handles
 *  pide las DOS clases (`.leaflet-div-icon.snap-vtx`, en `aoiMap.css`), de modo
 *  que con sólo `snap-vtx` no matcheaba ninguna regla: el handle existía en el
 *  DOM como un `<div>` sin borde, sin fondo y sin `border-radius`, o sea
 *  invisible. El síntoma era "el modo editar no muestra los vértices". */
export const VTX_ICON = L.divIcon({
  className: "leaflet-div-icon snap-vtx",
  iconSize: [SNAP_PX_ICONO, SNAP_PX_ICONO],
});

/** El handle del punto medio: MISMO aspecto que el de un vértice real —verde,
 *  borde naranja, mismo tamaño—, porque es lo mismo que se engancha y se
 *  arrastra.
 *
 *  Lo único que lo distingue es el tooltip del `title`, que es donde el que no
 *  conoce la herramienta averigua qué hace cada uno. Antes lo distinguía también
 *  el cursor (`copy` acá contra `grab` en un vértice); los dos pasaron a
 *  `pointer`, el mismo de todo lo que se toca en estos mapas (ver `aoiMap.css`).
 *  Lleva igual las DOS clases: de `snap-vtx` saca el aspecto, y `snap-mid` queda
 *  como gancho sin estilo propio. */
export const MID_ICON = L.divIcon({
  className: "leaflet-div-icon snap-vtx snap-mid",
  iconSize: [SNAP_PX_ICONO, SNAP_PX_ICONO],
});

/** Una operación sobre UN vértice, lista para mandar al servidor.
 *
 *  Las tres formas se distinguen por los campos, igual que el request de
 *  `EditarVerticePoligonoPadre`:
 *   - **mover**: `origen` y `destino`;
 *   - **quitar**: sólo `origen`;
 *   - **insertar**: `insercion` y `destino` — una sola operación, porque así la
 *     vive el usuario: agarra el punto medio de un tramo y lo arrastra.
 *
 *  Es el mismo contrato que `OperacionVertice` del paso 4
 *  (`LoteVertexEditEngine`); el paso 3 lo tiene aparte porque nombra la pieza,
 *  no el lote, y porque necesita decir en qué anillo pasó. */
export type OperacionVerticeAoi = {
  origen?: Position;
  destino?: Position;
  insercion?: { indiceArista: number };
  /** Anillo donde ocurrió: 0 es el exterior. Una pieza del paso 3 no tiene
   *  interiores, pero el dato viaja para que quien persista pueda descartar
   *  una operación que el servidor no sabe aplicar (insertar solo opera sobre
   *  el exterior). */
  anillo: number;
};

export interface AoiSnapEditOpciones {
  map: L.Map;
  /** La geometría cambió y hay que guardarla. Recibe el polígono completo y la
   *  OPERACIÓN que lo produjo.
   *
   *  La operación es lo que se manda al servidor (`EditarVerticePoligonoPadre`):
   *  un punto, no la geometría entera. El polígono sigue viniendo porque quien
   *  escucha lo usa para el área en vivo y como respaldo cuando la pieza
   *  todavía no tiene id en la base. */
  onCambio: (poligono: Polygon, operacion: OperacionVerticeAoi) => void;
  /** Una operación se rechazó. Con el polígono sin dibujar del lado del
   *  cliente, un rechazo silencioso se ve como "no pasó nada". */
  onRechazado?: (motivo: string) => void;
  /** ¿La forma que dejó la operación es válida? Devuelve el motivo del rechazo,
   *  o `null` si se acepta.
   *
   *  Es un chequeo PREVIO y barato (el auto-cruce), no la validación final: esa
   *  la hace el servidor al aplicar la operación
   *  (`EditarVerticePoligonoPadre`), que es el que mide el solape contra las
   *  piezas vecinas. Acá no se compara con ninguna otra geometría; este motor
   *  sólo sabe deshacer la operación si la respuesta viene en contra.
   *
   *  Sigue siendo asincrónica porque el contrato no cambió y porque quien
   *  escucha puede querer volver a preguntarle a la red.
   *
   *  Sin este callback el motor acepta todo, como antes. */
  validar?: (poligono: Polygon) => Promise<string | null>;
}

/** Edición de vértices por SNAP, sin dibujar el polígono.
 *
 *  **Hay UN handle en pantalla, nunca más de uno.** Es el punto enganchado: de
 *  los tres candidatos del tramo bajo el cursor —sus dos extremos y su punto
 *  medio— se queda el más cercano, y los otros dos no se dibujan. Antes se
 *  pintaban los tres a la vez y eso no es un snap: con tres puntos iguales
 *  encima del tramo no se sabe cuál se va a agarrar, y los tres tapan los
 *  vértices en reposo que dibuja el WMS.
 *
 *  El enganche se ve porque el handle NO se parece al vértice en reposo: éste
 *  es verde y 2 px más grande (ver `SNAP_PX_ICONO`), los de reposo son blancos
 *  con borde naranja y los pinta GeoServer.
 *
 *  **Qué cambia respecto del modo Editar anterior.** Aquel ponía un marcador
 *  por cada vértice del polígono bajo el cursor y además lo repintaba en
 *  Leaflet. Acá no se dibuja el polígono —ya lo renderiza el WMS de polígonos
 *  padre—.
 *
 *  **Cómo se resuelve sin tocar el DOM.** Al seleccionar la pieza, sus
 *  vértices se proyectan UNA vez a píxeles en arrays planos (`Float64Array`).
 *  En cada `mousemove` se busca el segmento más cercano con aritmética pura
 *  sobre esos arrays; el DOM sólo se toca cuando el tramo activo CAMBIA, que es
 *  lo que controla `_hoverKey`.
 *
 *  **Las tres operaciones** son las del modo anterior, y se conservan enteras:
 *   - arrastrar un vértice lo mueve;
 *   - arrastrar el punto medio inserta ahí un vértice y lo mueve;
 *   - click sobre un vértice lo elimina.
 *
 *  Con un solo handle siguen alcanzando todas: acercando el cursor a un extremo
 *  engancha ese vértice (arrastrar / click), y acercándolo al centro del tramo
 *  engancha el punto medio (arrastrar para insertar).
 *
 *  **No se dibuja NINGUNA línea del tramo.** Había un `L.polyline` celeste
 *  sobre el tramo activo y era un bug doble: se creaba sin `pane`, así que caía
 *  en el `overlayPane` (z-index 400) y quedaba DEBAJO del pane del AOI (450) —
 *  se veía como una línea azul por detrás del polígono padre, nunca al frente—
 *  y además duplicaba el contorno que el WMS ya pinta. Lo que señala qué se va
 *  a editar es el handle enganchado y nada más.
 */
export class AoiSnapEditEngine {
  constructor(private readonly _o: AoiSnapEditOpciones) {}

  /** Pone en edición la pieza indicada. La geometría llega de afuera (la lee
   *  el llamador por gRPC); este motor no consulta servicios. */
  seleccionar(poligono: Polygon): void {
    this.cerrar();
    this._anillos = poligono.coordinates.map((anillo) => {
      const puntos = anillo.slice();
      // Se descarta el punto de cierre: el anillo se maneja como cíclico y se
      // vuelve a cerrar al exportar.
      const n = puntos.length;
      if (
        n > 1 &&
        puntos[0][0] === puntos[n - 1][0] &&
        puntos[0][1] === puntos[n - 1][1]
      ) {
        puntos.pop();
      }
      return puntos;
    });
    this._proyectar();
    this._activo = true;
  }

  /** Enciende el modo. Sin pieza seleccionada no hace nada hasta que llegue
   *  una por `seleccionar`. */
  activar(): void {
    if (this._enganchado) return;
    this._o.map.on("mousemove", this._onMouseMove);
    this._o.map.on("zoomend moveend", this._onVistaCambio);
    this._o.map.getContainer().addEventListener("mouseleave", this._onSalir);
    this._enganchado = true;
  }

  desactivar(): void {
    this.cerrar();
    if (!this._enganchado) return;
    this._o.map.off("mousemove", this._onMouseMove);
    this._o.map.off("zoomend moveend", this._onVistaCambio);
    this._o.map.getContainer().removeEventListener("mouseleave", this._onSalir);
    this._enganchado = false;
  }

  /** Suelta la pieza en edición y limpia los handles. */
  cerrar(): void {
    this._limpiarHandles();
    this._anillos = [];
    this._cache = [];
    this._activo = false;
    // Invalida cualquier validación en vuelo: su respuesta ya no aplica a nada
    // (`_confirmar` la descarta por token, además del chequeo de `_activo`).
    this._operacion++;
  }

  get hayPieza(): boolean {
    return this._activo;
  }

  dispose(): void {
    this.desactivar();
  }

  // ---------------------------------------------------------------- estado
  private _anillos: Position[][] = [];
  private _cache: { ri: number; n: number; xs: Float64Array; ys: Float64Array }[] = [];
  private _handles: L.Marker[] = [];
  private _hoverKey = "";
  private _cursor: L.Point | null = null;
  private _activo = false;
  private _enganchado = false;
  private _arrastrando = false;
  /** Cuenta las operaciones confirmadas, para descartar la respuesta de una
   *  validación que quedó vieja — ver `_confirmar`. */
  private _operacion = 0;
  private _sobreHandle = false;
  private _rafPendiente = false;

  // ------------------------------------------------------------ proyección
  /** Vértices a píxeles, una vez por vista. Es lo que permite resolver el snap
   *  con aritmética y sin recorrer capas de Leaflet. */
  private _proyectar(): void {
    this._cache = this._anillos.map((anillo, ri) => {
      const n = anillo.length;
      const xs = new Float64Array(n);
      const ys = new Float64Array(n);
      for (let i = 0; i < n; i++) {
        const p = this._o.map.latLngToLayerPoint(
          L.latLng(anillo[i][1], anillo[i][0]),
        );
        xs[i] = p.x;
        ys[i] = p.y;
      }
      return { ri, n, xs, ys };
    });
  }

  /** Distancia² de un punto a un segmento. Al cuadrado para no sacar raíz
   *  dentro del bucle: sólo se comparan entre sí. */
  private static _dist2(
    px: number, py: number, ax: number, ay: number, bx: number, by: number,
  ): number {
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = 0;
    if (len2 > 0) t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
    const ex = px - (ax + t * dx);
    const ey = py - (ay + t * dy);
    return ex * ex + ey * ey;
  }

  // -------------------------------------------------------------- eventos
  private _onMouseMove = (ev: L.LeafletMouseEvent): void => {
    this._cursor = ev.layerPoint;
    // Se recalcula en rAF: el navegador dispara muchos más `mousemove` que
    // cuadros, y resolver el snap una vez por cuadro alcanza.
    if (this._rafPendiente) return;
    this._rafPendiente = true;
    requestAnimationFrame(() => {
      this._rafPendiente = false;
      this._refrescar(false);
    });
  };

  private _onVistaCambio = (): void => {
    if (!this._activo) return;
    this._proyectar();
    this._refrescar(true);
  };

  /** `mouseleave` del contenedor y NO el `mouseout` del mapa: `mouseout`
   *  también salta al pasar sobre un handle, y borraría el marcador justo
   *  cuando el usuario va a agarrarlo. */
  private _onSalir = (): void => {
    this._cursor = null;
    if (!this._arrastrando) this._limpiarHandles();
  };

  // ----------------------------------------------------------- los handles
  private _refrescar(forzar: boolean): void {
    if (!this._activo || this._arrastrando) return;
    // Con el cursor sobre un handle no se recalcula: podría quedar más cerca
    // de otro tramo y el handle desaparecería al ir a tomarlo.
    if (this._sobreHandle && !forzar) return;
    if (!this._cursor) {
      if (this._hoverKey) this._limpiarHandles();
      return;
    }

    const tol2 = SNAP_PX * SNAP_PX;
    const mx = this._cursor.x;
    const my = this._cursor.y;
    let mejor: {
      ri: number; i: number; j: number;
      ax: number; ay: number; bx: number; by: number;
    } | null = null;
    let mejorD = tol2;

    for (const rc of this._cache) {
      for (let i = 0; i < rc.n; i++) {
        const j = (i + 1) % rc.n;
        const ax = rc.xs[i], ay = rc.ys[i], bx = rc.xs[j], by = rc.ys[j];
        // Descarte por caja antes de medir: es lo que deja el bucle barato con
        // miles de vértices.
        if (
          mx < Math.min(ax, bx) - SNAP_PX || mx > Math.max(ax, bx) + SNAP_PX ||
          my < Math.min(ay, by) - SNAP_PX || my > Math.max(ay, by) + SNAP_PX
        ) continue;
        const d = AoiSnapEditEngine._dist2(mx, my, ax, ay, bx, by);
        if (d < mejorD) {
          mejorD = d;
          mejor = { ri: rc.ri, i, j, ax, ay, bx, by };
        }
      }
    }

    if (!mejor) {
      if (this._hoverKey) this._limpiarHandles();
      return;
    }

    // El tramo decide QUÉ tres puntos se pueden enganchar; el cursor decide
    // cuál de los tres. Se mide contra los puntos, no contra el tramo: el
    // tramo ya pasó el filtro de tolerancia de arriba.
    const candidato = AoiSnapEditEngine._masCercano(mx, my, mejor);

    // La clave incluye el candidato, no sólo el tramo: moviendo el cursor a lo
    // largo del mismo tramo el enganche salta de un punto a otro, y eso SÍ
    // cambia lo que hay en pantalla. (Cuando se dibujaban los tres handles a la
    // vez, incluirlo sólo reconstruía marcadores idénticos.)
    const clave = `${mejor.ri}/${mejor.i}-${mejor.j}/${candidato}`;
    if (clave === this._hoverKey && !forzar) return; // nada cambió: no tocar el DOM
    this._hoverKey = clave;

    this._limpiarHandles(true);
    const anillo = this._anillos[mejor.ri];

    // UN handle, el del punto enganchado.
    this._handles.push(
      candidato === "medio"
        ? this._handleMedio(mejor.ri, mejor.i, anillo[mejor.i], anillo[mejor.j])
        : this._handleVertice(
            mejor.ri,
            candidato === "a" ? mejor.i : mejor.j,
          ),
    );
  }

  /** De los tres puntos enganchables de un tramo —sus dos extremos y su punto
   *  medio—, el más cercano al cursor.
   *
   *  Las distancias van al cuadrado: sólo se comparan entre sí, así que la raíz
   *  no aporta nada. Los empates caen del lado del extremo `a`, y da igual cuál
   *  gane: a esa distancia los dos puntos están a menos de un píxel. */
  private static _masCercano(
    mx: number,
    my: number,
    t: { ax: number; ay: number; bx: number; by: number },
  ): "a" | "b" | "medio" {
    const d2 = (x: number, y: number): number =>
      (mx - x) * (mx - x) + (my - y) * (my - y);

    const dA = d2(t.ax, t.ay);
    const dB = d2(t.bx, t.by);
    const dM = d2((t.ax + t.bx) / 2, (t.ay + t.by) / 2);

    if (dA <= dB && dA <= dM) return "a";
    if (dB <= dM) return "b";
    return "medio";
  }

  /** Saca el handle del mapa. Sigue siendo un arreglo y no un único marcador
   *  porque el alta y la baja son el mismo código para el handle de un vértice
   *  y para el del punto medio, y porque el arrastre del medio reemplaza uno
   *  por otro — con un campo suelto habría que acordarse de limpiarlo antes de
   *  pisarlo. */
  private _limpiarHandles(conservarClave = false): void {
    this._handles.forEach((m) => this._o.map.removeLayer(m));
    this._handles = [];
    this._sobreHandle = false;
    if (!conservarClave) this._hoverKey = "";
  }

  /** Engancha a un handle lo que necesita para no pelearse con el mapa.
   *
   *  **`mousedown` y `mouseup` NO pueden llegar al mapa**, y esto es lo que
   *  arreglaba el bug de "click en un vértice no lo borra, deselecciona la
   *  pieza".
   *
   *  El modo Editar resuelve con QUÉ pieza trabajar escuchando el `mousedown` /
   *  `mouseup` del MAPA (las piezas guardadas no son capas de Leaflet, así que no
   *  hay un `layer.on("click")` que lo diga — ver `AoiDrawEngine._alClickDePieza`).
   *  Y un marcador de Leaflet sólo "captura" los tipos de evento que escucha:
   *  `Map._findEventTargets` recorre el DOM buscando una capa que escuche ESE
   *  tipo y, si no encuentra ninguna, le entrega el evento al mapa. Estos handles
   *  escuchaban `click`, `drag*`, `mouseover` y `mouseout`, pero no
   *  `mousedown`/`mouseup`: esos dos subían al mapa, que los leía como un click
   *  sobre la pieza y, siendo la misma que ya estaba en edición, la soltaba. El
   *  `L.DomEvent.stop` del handler de `click` llegaba tarde — `click` se dispara
   *  DESPUÉS de `mouseup`, cuando la pieza ya se había deseleccionado.
   *
   *  Con sólo escuchar esos dos tipos, el handle pasa a ser el destino y el mapa
   *  ya no los ve; el `L.DomEvent.stop` es el refuerzo, no es lo que resuelve.
   *
   *  **No rompe el arrastre.** `L.Draggable` escucha el `mousedown` directo en el
   *  elemento del marcador, no por este camino, y ya corrió cuando esto se
   *  ejecuta; el paneo del mapa tampoco se dispara, porque para entonces
   *  `Draggable._dragging` ya está tomado por el marcador. */
  private _enfocar(m: L.Marker): void {
    m.on("mouseover", () => { this._sobreHandle = true; });
    m.on("mouseout", () => { this._sobreHandle = false; });
    m.on("mousedown", (ev: L.LeafletMouseEvent) => L.DomEvent.stop(ev));
    m.on("mouseup", (ev: L.LeafletMouseEvent) => L.DomEvent.stop(ev));
  }

  private _handleVertice(ri: number, vi: number): L.Marker {
    const p = this._anillos[ri][vi];
    const m = L.marker(L.latLng(p[1], p[0]), {
      icon: VTX_ICON,
      draggable: true,
      keyboard: false,
      zIndexOffset: 1000,
      title: "Arrastrar = mover · click = eliminar",
    }).addTo(this._o.map);

    // El anillo tal como estaba antes de mover, para volver atrás si la
    // operación se rechaza.
    let previo: Position[][] = [];
    // Dónde estaba el vértice antes de arrastrarlo: es cómo lo nombra el
    // servidor (por coordenadas, no por id — ver `EditarVerticePoligonoPadre`).
    let origen: Position = [...p];
    m.on("dragstart", () => {
      this._arrastrando = true;
      previo = this._clonar();
      origen = [...this._anillos[ri][vi]];
    });
    m.on("drag", (e) => {
      // Durante el arrastre sólo se reescribe la coordenada: no se valida, no
      // se guarda, no se repinta nada. El handle lo mueve Leaflet solo, y el
      // contorno es del WMS, que recién se refresca al soltar.
      const ll = (e.target as L.Marker).getLatLng();
      this._anillos[ri][vi] = [ll.lng, ll.lat];
    });
    m.on("dragend", () => {
      this._arrastrando = false;
      this._confirmar(previo, {
        origen,
        destino: [...this._anillos[ri][vi]],
        anillo: ri,
      });
    });

    // Click = eliminar. Leaflet no dispara `click` tras un arrastre, así que
    // soltar el vértice no lo borra.
    m.on("click", (ev: L.LeafletMouseEvent) => {
      L.DomEvent.stop(ev);
      this._eliminarVertice(ri, vi);
    });

    this._enfocar(m);
    return m;
  }

  /** Handle "fantasma" en el medio del tramo. Al arrastrarlo se inserta ahí un
   *  vértice real y desde ese momento se mueve como cualquier otro. */
  private _handleMedio(ri: number, i: number, a: Position, b: Position): L.Marker {
    const medio = L.latLng((a[1] + b[1]) / 2, (a[0] + b[0]) / 2);
    const m = L.marker(medio, {
      icon: MID_ICON,
      draggable: true,
      keyboard: false,
      zIndexOffset: 900,
      title: "Arrastrar = agregar vértice",
    }).addTo(this._o.map);

    let insertado = -1;
    // El anillo SIN el vértice nuevo: deshacer la operación es volver a éste.
    let previo: Position[][] = [];
    m.on("dragstart", () => {
      this._arrastrando = true;
      previo = this._clonar();
      // Se inserta ANTES de mover: a partir de acá el índice existe y el
      // arrastre lo trata como un vértice más.
      insertado = i + 1;
      this._anillos[ri].splice(insertado, 0, [medio.lng, medio.lat]);
      this._proyectar();
    });
    m.on("drag", (e) => {
      if (insertado < 0) return;
      const ll = (e.target as L.Marker).getLatLng();
      this._anillos[ri][insertado] = [ll.lng, ll.lat];
    });
    m.on("dragend", () => {
      this._arrastrando = false;
      // El destino se lee ANTES de soltar el índice: después `insertado` ya no
      // apunta a nada.
      const destino: Position =
        insertado >= 0 ? [...this._anillos[ri][insertado]] : [medio.lng, medio.lat];
      insertado = -1;
      this._confirmar(previo, {
        // El servidor parte la arista `i` por su punto medio y mueve ese punto
        // al destino, en una sola operación: es exactamente lo que acaba de
        // pasar acá.
        insercion: { indiceArista: i },
        destino,
        anillo: ri,
      });
    });

    this._enfocar(m);
    return m;
  }

  private _eliminarVertice(ri: number, vi: number): void {
    // Un anillo necesita 3 vértices para seguir siendo un polígono.
    if (this._anillos[ri].length <= 3) {
      this._o.onRechazado?.(
        "Ese vértice no se puede quitar: el polígono quedaría con menos de tres esquinas.",
      );
      return;
    }
    const previo = this._clonar();
    const origen: Position = [...this._anillos[ri][vi]];
    this._anillos[ri].splice(vi, 1);
    this._confirmar(previo, { origen, anillo: ri });
  }

  /** Cierra una operación: la manda a validar y, según la respuesta, la confirma
   *  o la DESHACE. El repintado de la forma lo hace el WMS, no este motor.
   *
   *  **`previo` es el anillo tal como estaba antes de la operación**, que es lo
   *  único que hace falta para deshacerla. Se guarda una copia completa en vez
   *  de anotar qué cambió: las tres operaciones —mover, insertar, quitar—
   *  vuelven atrás igual, sin un camino de reversa por cada una. La copia se
   *  saca UNA vez por operación (no por cuadro de arrastre), así que no se mide.
   *
   *  **Es optimista**: los handles quedan en la posición nueva sin esperar la
   *  respuesta, y si el servidor la rechaza vuelven solos. Esperar con los
   *  handles apagados se sentiría como un tirón en cada vértice que se suelta.
   *
   *  **Nada se guarda antes de la respuesta.** `onCambio` es lo que dispara el
   *  `GuardarAoi`, y sólo sale si la forma pasó. Un rechazo no llega nunca a la
   *  base. */
  private _confirmar(previo: Position[][], operacion: OperacionVerticeAoi): void {
    const poligono = this.geometria();
    this._proyectar();
    this._limpiarHandles();
    this._refrescar(true);

    const validar = this._o.validar;
    if (!validar) {
      this._o.onCambio(poligono, operacion);
      return;
    }

    // Identifica ESTA operación. Si mientras viajaba la respuesta el usuario
    // movió otro vértice, o soltó la pieza, lo que vuelve ya no corresponde:
    // deshacer ahí pisaría un cambio más nuevo con un anillo viejo.
    const token = ++this._operacion;
    void validar(poligono).then((motivo) => {
      if (token !== this._operacion || !this._activo) return;

      if (!motivo) {
        this._o.onCambio(poligono, operacion);
        return;
      }

      this._anillos = previo;
      this._proyectar();
      this._limpiarHandles();
      this._refrescar(true);
      this._o.onRechazado?.(motivo);
    });
  }

  /** Copia de los anillos, para poder deshacer una operación. */
  private _clonar(): Position[][] {
    return this._anillos.map((anillo) => anillo.map((p) => [...p]));
  }

  /** El polígono actual, con los anillos cerrados como pide GeoJSON. */
  geometria(): Polygon {
    return {
      type: "Polygon",
      coordinates: this._anillos.map((anillo) => [...anillo, anillo[0]]),
    };
  }
}
